import {
  ASSISTANT_IMAGE_CREDITS,
  CREATIVE_FORMATS,
  CREATIVE_FORMAT_LIMITS,
  creativeFormatForSize,
} from '@digital-twins/shared-types'

import type { AssistantImageQuality, CreativeFormatId } from '@digital-twins/shared-types'
import type { AssistantImageRequest } from '@digital-twins/shared-types/assistant'
import type { ImageApprovalPair } from '@/types/components/thread-image-approval'

export interface ImageApprovalGroupTurn {
  id: string
  serverToken?: string
  role: 'user' | 'assistant'
  imageRequests?: Array<AssistantImageRequest>
  imageApprovalRequestId?: string
  imageApprovalFormatId?: string
}

export interface ImageApprovalGroup<T extends ImageApprovalGroupTurn> {
  /** The approval card's message token. */
  cardToken: string
  /** The admitted requests, in approval order, as the card holds them. */
  requests: Array<AssistantImageRequest>
  /** The admitted images, one per request and format: what the batch's ask counts. */
  pairs: Array<{ request: AssistantImageRequest; formatId?: string }>
  /** Every approval answer of the batch, rendered together. */
  imageTurns: Array<T>
  /** The card holds these images itself: no ask or answer shows in the thread. */
  isOnCard: boolean
}

/** `bubble`: the batch's one user ask; `images`: the batch's one image block; `hidden`: folded into those. */
export type ImageApprovalPlacement = 'bubble' | 'images' | 'hidden'

export interface ImageApprovalTurnSlot<T extends ImageApprovalGroupTurn> {
  group: ImageApprovalGroup<T>
  placement: ImageApprovalPlacement
}

/**
 * Folds consecutive approval turns of one image request set into one batch.
 * Each approval is its own user ask and answer (one paid turn per image); the
 * reader sees one ask and one block of images. Keyed only on the request ids
 * the turns carry, live (`runAsk`) and on reload (`imageApprovalRequestId`).
 */
export function imageApprovalSlots<T extends ImageApprovalGroupTurn>(
  turns: Array<T>,
  isOnCard?: (requests: Array<AssistantImageRequest>, cardToken: string) => boolean,
): Map<string, ImageApprovalTurnSlot<T>> {
  const cardByRequest = new Map<string, { cardToken: string; request: AssistantImageRequest; rows: Array<AssistantImageRequest> }>()
  for (const turn of turns) {
    for (const request of turn.imageRequests ?? []) {
      cardByRequest.set(request.requestId, { cardToken: turn.serverToken ?? turn.id, request, rows: turn.imageRequests ?? [] })
    }
  }
  const slots = new Map<string, ImageApprovalTurnSlot<T>>()
  let open: { group: ImageApprovalGroup<T>; hasBubble: boolean } | null = null
  for (const turn of turns) {
    const card = turn.imageApprovalRequestId ? cardByRequest.get(turn.imageApprovalRequestId) : undefined
    if (!card) {
      open = null
      continue
    }
    if (!open || open.group.cardToken !== card.cardToken) {
      open = {
        group: {
          cardToken: card.cardToken,
          requests: [],
          pairs: [],
          imageTurns: [],
          isOnCard: isOnCard?.(card.rows, card.cardToken) ?? false,
        },
        hasBubble: false,
      }
    }
    const { group } = open
    if (!group.requests.some((request) => request.requestId === card.request.requestId)) {
      group.requests.push(card.request)
    }
    const formatId = turn.imageApprovalFormatId
    if (!group.pairs.some((pair) => pair.request.requestId === card.request.requestId && pair.formatId === formatId)) {
      group.pairs.push({ request: card.request, formatId })
    }
    let placement: ImageApprovalPlacement = 'hidden'
    if (turn.role === 'user') {
      placement = open.hasBubble ? 'hidden' : 'bubble'
      open.hasBubble = true
    } else {
      placement = group.imageTurns.length === 0 ? 'images' : 'hidden'
      group.imageTurns.push(turn)
    }
    slots.set(turn.id, { group, placement: group.isOnCard ? 'hidden' : placement })
  }
  return slots
}

/** A card with more than one image: rows times the running or saved selection. */
export function isImageApprovalSet(
  requests: ReadonlyArray<AssistantImageRequest>,
  batchFormats: ReadonlyArray<CreativeFormatId> | null | undefined,
): boolean {
  const formats = batchFormats ?? requests.find((request) => request.selectedFormats?.length)?.selectedFormats
  return requests.length * (formats?.length ?? 1) > 1
}

/** What each card holds itself, whole even when a chat turn landed between the first image and the rest. */
export function imageApprovalCardGroups<T extends ImageApprovalGroupTurn>(
  slots: Map<string, ImageApprovalTurnSlot<T>>,
): Map<string, ImageApprovalGroup<T>> {
  const cards = new Map<string, ImageApprovalGroup<T>>()
  const seen = new Set<ImageApprovalGroup<T>>()
  for (const { group } of slots.values()) {
    if (!group.isOnCard || seen.has(group)) {
      continue
    }
    seen.add(group)
    const card = cards.get(group.cardToken)
    if (!card) {
      cards.set(group.cardToken, group)
    } else {
      cards.set(group.cardToken, {
        ...card,
        requests: [...card.requests, ...group.requests.filter((request) => !card.requests.includes(request))],
        pairs: [...card.pairs, ...group.pairs],
        imageTurns: [...card.imageTurns, ...group.imageTurns],
      })
    }
  }
  return cards
}

/** Credits the batch admitted: one unit per image at the admitted quality. */
export function imageApprovalCredits(
  requests: Array<AssistantImageRequest>,
  quality?: AssistantImageQuality,
): number {
  return requests.reduce(
    (sum, request) => sum + ASSISTANT_IMAGE_CREDITS[quality ?? request.arguments.quality],
    0,
  )
}

/** The format a request was quoted in, or null for a saved size off the list. */
export function quotedImageFormatId(request: AssistantImageRequest): CreativeFormatId | null {
  return creativeFormatForSize(request.arguments.size)?.id ?? null
}

/**
 * A selection in the list's order, whatever order it was ticked in, with the quoted format
 * first when it is ticked: that is the one the user named first, so it is the first image.
 */
export function orderedImageFormats(
  formats: ReadonlyArray<CreativeFormatId>,
  quotedFormatId?: CreativeFormatId | null,
): Array<CreativeFormatId> {
  const ordered = CREATIVE_FORMATS.map((format) => format.id).filter((id) => formats.includes(id))
  return quotedFormatId && ordered.includes(quotedFormatId)
    ? [quotedFormatId, ...ordered.filter((id) => id !== quotedFormatId)]
    : ordered
}

/** Whether one more format stays inside the caps for a card of this many rows. */
export function canAddImageFormat(rows: number, selected: number): boolean {
  return (
    selected < CREATIVE_FORMAT_LIMITS.formatsPerRequest &&
    rows * (selected + 1) <= CREATIVE_FORMAT_LIMITS.imagesPerRequest
  )
}

/** The formats a quote named: the quoted one plus the others the model listed, inside the caps. */
export function requestedImageFormats(
  requests: ReadonlyArray<AssistantImageRequest>,
  quotedFormatId: CreativeFormatId,
): Array<CreativeFormatId> {
  const selected: Array<CreativeFormatId> = [quotedFormatId]
  // A card has one selection for all its rows: only what every row named starts ticked, so no row pays for another's.
  const namedByAll = (requests[0]?.arguments.other_sizes ?? []).filter((size) =>
    requests.every((request) => request.arguments.other_sizes?.includes(size)))
  for (const size of namedByAll) {
    const id = creativeFormatForSize(size)?.id
    if (id && !selected.includes(id) && canAddImageFormat(requests.length, selected.length)) {
      selected.push(id)
    }
  }
  return orderedImageFormats(selected, quotedFormatId)
}

function ownImageFormatStatus(
  request: AssistantImageRequest,
  formatId: CreativeFormatId,
): AssistantImageRequest['executionStatus'] {
  const execution = request.formatExecutions?.find((item) => item.formatId === formatId)
  if (execution) {
    return execution.status
  }
  // Turns from before formats report only the quoted size's status.
  return formatId === quotedImageFormatId(request) ? (request.executionStatus ?? null) : null
}

/** The first format is the image's master: when it fails or is stopped, the others of that image never run. */
function imageFormatStatus(
  request: AssistantImageRequest,
  formatId: CreativeFormatId,
  masterFormatId: CreativeFormatId,
): AssistantImageRequest['executionStatus'] {
  const own = ownImageFormatStatus(request, formatId)
  if (own || formatId === masterFormatId) {
    return own
  }
  const master = ownImageFormatStatus(request, masterFormatId)
  return master === 'failed' || master === 'cancelled' ? master : null
}

/**
 * Every image the card stands for, row by row. Without a selection (mixed sizes, a quote
 * to redo) a row is one image in its own size, as it always was.
 */
export function imageApprovalPairs(
  requests: ReadonlyArray<AssistantImageRequest>,
  formats: ReadonlyArray<CreativeFormatId> | null,
): Array<ImageApprovalPair> {
  return requests.flatMap((request): Array<ImageApprovalPair> =>
    formats
      ? formats.map((formatId) => ({ request, formatId, status: imageFormatStatus(request, formatId, formats[0]) }))
      : [{ request, formatId: null, status: request.executionStatus ?? null }],
  )
}

/** What the server saved on an approval: it wins over whatever this tab holds. */
export interface ImageApprovalSaved {
  quality?: AssistantImageQuality
  formats?: Array<CreativeFormatId>
  /** Which try of the image's first format this turn is. Absent on its other formats. */
  tryNumber?: number
  /** Which image of the card this turn is. */
  step?: 'first' | 'try' | 'rest'
  /** The brand this click rendered under, known to the tab that clicked. */
  brandToken?: string | null
  /** What this click rendered from, when the card's choices changed its quote. Null: the quote itself. */
  render?: { summary: AssistantImageRequest['contextSummary']; purpose: string } | null
}

/** Live only: how the image last ended, kept while a try runs in case the try is refused. */
type LiveImageRequest = AssistantImageRequest & {
  lastEnding?: 'failed' | 'cancelled'
  /** Live only: what the row named before a turn started rendering, back if that turn makes no image. */
  madeRender?: { summary: AssistantImageRequest['imageSummary']; purpose: AssistantImageRequest['imagePurpose'] }
}

/**
 * The live status of one approval turn on its request; only the quoted format owns `executionStatus`.
 * A null status clears the format: an attempt that never rendered leaves no trace on the card.
 */
export function withImageRequestStatus(
  request: LiveImageRequest,
  status: AssistantImageRequest['executionStatus'],
  formatId?: string,
  saved?: ImageApprovalSaved,
): AssistantImageRequest {
  const format = CREATIVE_FORMATS.find((candidate) => candidate.id === formatId)
  const ownStatus = format ? request.formatExecutions?.find((item) => item.formatId === format.id)?.status : request.executionStatus
  const lastEnding = ownStatus === 'failed' || ownStatus === 'cancelled' ? ownStatus : request.lastEnding
  const isNewestImage = status === 'completed' && saved?.step !== 'rest' && saved?.brandToken !== undefined
  const tryNumber = saved?.tryNumber
  const completedTries = request.completedTries ?? 0
  // A try that ends with no image leaves the last image standing.
  const hasEarlierImage = Boolean(tryNumber && tryNumber > 1 && completedTries > 0)
  const isOver = status === null || status === 'failed' || status === 'cancelled'
  // A try refused at the quote changes nothing: with no image yet, the last real ending stands.
  const hasEarlierFailure = Boolean(tryNumber && tryNumber > 1) && !hasEarlierImage && status === null
  const shown = hasEarlierImage && isOver ? 'completed' : hasEarlierFailure ? (lastEnding ?? 'failed') : status
  const isRendering = status === 'queued' || status === 'running'
  const withSaved: LiveImageRequest = {
    ...request,
    ...(lastEnding ? { lastEnding } : {}),
    ...(isNewestImage ? { imageBrandToken: saved?.brandToken ?? '' } : {}),
    ...(isRendering && saved?.render !== undefined
      ? { madeRender: request.madeRender ?? { summary: request.imageSummary ?? null, purpose: request.imagePurpose ?? null } }
      : {}),
    ...((status === 'completed' || isRendering) && saved?.render !== undefined
      ? { imageSummary: saved.render?.summary ?? null, imagePurpose: saved.render?.purpose ?? null }
      : {}),
    ...(isOver && request.madeRender
      ? { imageSummary: request.madeRender.summary, imagePurpose: request.madeRender.purpose, madeRender: undefined }
      : {}),
    ...(status === 'completed' ? { madeRender: undefined } : {}),
    ...(saved?.quality
      ? {
          arguments: { ...request.arguments, quality: saved.quality },
          credits: ASSISTANT_IMAGE_CREDITS[saved.quality],
        }
      : {}),
    ...(saved?.formats ? { selectedFormats: saved.formats } : {}),
    ...(tryNumber
      ? {
          imageTries: status === null ? tryNumber - 1 : Math.max(request.imageTries ?? 0, tryNumber),
          completedTries: status === 'completed' ? Math.max(completedTries, tryNumber) : completedTries,
        }
      : {}),
    ...(saved?.step === 'rest' ? { restStarted: status !== null } : {}),
  }
  const next: AssistantImageRequest = !format
    ? { ...withSaved, executionStatus: shown }
    : {
        ...withSaved,
        ...(format.id === quotedImageFormatId(request) ? { executionStatus: shown } : {}),
        formatExecutions: [
          ...(request.formatExecutions ?? []).filter((item) => item.formatId !== format.id),
          ...(shown ? [{ formatId: format.id, status: shown }] : []),
        ],
      }
  // Nothing started on this image after all: its attempt locks no selection.
  return status === null && !next.executionStatus && !next.formatExecutions?.length ? { ...next, selectedFormats: null } : next
}

/** The first image, while it can still be made again: one image made and nothing else, or only failed first images, before the rest started. */
export function imageRetryTarget(
  requests: ReadonlyArray<AssistantImageRequest>,
  formats: ReadonlyArray<CreativeFormatId> | null,
): (ImageApprovalPair & { hasImage: boolean }) | null {
  if (requests.some((request) => request.restStarted)) {
    return null
  }
  const admitted = requests.flatMap((request) =>
    (formats ?? [null]).flatMap((formatId) => {
      const status = formatId ? ownImageFormatStatus(request, formatId) : (request.executionStatus ?? null)
      return status ? [{ request, formatId, status, isFirst: !formats || formatId === formats[0] }] : []
    }),
  )
  if (admitted.length === 0 || admitted.some((item) => item.status === 'queued' || item.status === 'running' || !item.isFirst)) {
    return null
  }
  const made = admitted.filter((item) => item.status === 'completed')
  if (made.length > 1) {
    return null
  }
  const target = made[0] ?? admitted[admitted.length - 1]
  return { request: target.request, formatId: target.formatId, status: target.status, hasImage: made.length === 1 }
}

/** One image per request and format: the newest try, or the last image when a newer try ended without one. */
export function newestImageTurns<T extends { imageApprovalRequestId?: string; imageApprovalFormatId?: string; refused?: boolean; stopped?: boolean; imageProgress?: Record<string, { stage: string }> }>(
  turns: ReadonlyArray<T>,
): Array<T> {
  const newest = new Map<string, T>()
  for (const turn of turns) {
    const key = `${turn.imageApprovalRequestId}:${turn.imageApprovalFormatId}`
    const hasImage = Object.values(turn.imageProgress ?? {}).some((image) => image.stage === 'done')
    const isOver = (turn.refused || turn.stopped) && !hasImage
    if (!newest.has(key) || !isOver) {
      newest.set(key, turn)
    }
  }
  return [...newest.values()]
}
